import useSWR from 'swr';
import type { OfferT } from '@lambda/apis/src/catalog-v2/types';

export type OfferAllocationT = {
  items: OfferT[];
  availableQuantity: number;
};

const allocationUrl = (skuId: string, quantity: number) =>
  `/api/catalog-v2/skus/${encodeURIComponent(
    skuId,
  )}/offer-allocation?quantity=${quantity}`;

/**
 * The offers the catalogue would draw on to fulfil a given quantity, in the
 * order it wants them consumed. Which offers those are depends on how much is
 * being asked for, so a split has to ask for the amount it is about to write.
 */
export const fetchOfferAllocation = async (
  skuId: string,
  quantity: number,
): Promise<OfferAllocationT | null> => {
  const res = await fetch(allocationUrl(skuId, quantity));

  if (!res.ok) {
    return null;
  }

  return (await res.json()) as OfferAllocationT;
};

/**
 * How much of a SKU can be bought right now — the ceiling for its quantity
 * selector. `availableQuantity` is the total across every offer, so asking for
 * a single unit is enough to learn it, and that is one request per SKU however
 * many lines the cart holds for it.
 */
export default function useAvailableQuantity(
  skuId: string | undefined,
): number | undefined {
  const { data } = useSWR<OfferAllocationT>(
    skuId ? allocationUrl(skuId, 1) : null,
    {
      // Stock does move, but not usually while a cart is open, and refetching
      // per focus was costing a request per SKU every time.
      revalidateOnFocus: false,
      revalidateIfStale: false,
      dedupingInterval: 60_000,
    },
  );

  return data?.availableQuantity;
}
